import * as React from 'react';

import { trackFavorite } from '@hungryroot/favorites';
import { Bookmark } from '@hungryroot/icons';
import { type Customer } from '@hungryroot/rest';
import { useDeleteFavoritePairing } from '../../api/v2/customers/[id]/favorite_pairings/[id]/delete/delete-favorite-pairing';
import { useFavoritePairing } from '../../api/v2/customers/[id]/favorite_pairings/get/get-favorite-pairings';
import { usePostFavoritePairing } from '../../api/v2/customers/[id]/favorite_pairings/post/post-favorite-pairings';
import styles from './SaveRecipes.module.css';

export enum Variant {
  Card = 'card',
  RDP = 'rdp',
}

interface Props {
  testid?: string;
  pairingId: number;
  pairingName: string;
  segment: {
    location: 'card' | 'RDP';
    path: string;
  };
  className?: string;
  customer: Pick<Customer, 'id'>;
  variant: Variant;
  onFavoriteChange?: (isFavorite: boolean) => void;
  onFavorite?: () => void;
}

export function SaveRecipes(props: Props) {
  const {
    testid,
    className,
    pairingId,
    pairingName,
    segment,
    variant,
    customer,
    onFavoriteChange,
    onFavorite,
  } = props;

  const { isFavorited, isFetching } = useFavoritePairing(
    customer.id,
    pairingId
  );

  const postFavoritePairing = usePostFavoritePairing();
  const deleteFavoritePairing = useDeleteFavoritePairing();

  const { isPending: isPendingPostFavoritePairing } = postFavoritePairing;
  const { isPending: isPendingDeleteFavoritePairing } = deleteFavoritePairing;

  const isDisabled =
    isFetching ||
    isPendingPostFavoritePairing ||
    isPendingDeleteFavoritePairing;

  if (isFavorited === undefined) {
    return null;
  }

  const onClick = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled) {
      return;
    }

    const mutation = {
      data: { customerId: customer.id, pairingId: pairingId },
    };
    if (isFavorited) {
      deleteFavoritePairing.mutate(mutation);
    } else {
      onFavorite?.();
      postFavoritePairing.mutate(mutation);
    }

    trackFavorite({
      id: pairingId,
      name: pairingName,
      category: 'pairings',
      location: segment.location,
      state: !isFavorited,
    });

    if (onFavoriteChange) {
      onFavoriteChange(!isFavorited);
    }
  };

  const classes = `${className} ${styles.bookmark} ${styles[variant]} ${
    isFavorited ? styles.clicked : styles.default
  } ${isDisabled ? styles.disabled : ''}`;

  return (
    <div className={classes} data-testid={testid} onClick={(e) => onClick(e)}>
      <input
        type="hidden"
        data-testid={`${testid}-value`}
        value={isFavorited + ''}
        disabled={isDisabled}
      />
      <Bookmark />
    </div>
  );
}
